import { FC, forwardRef } from 'react';

import ChainIcon from '@/components/ChainIcon';
import { Button, type ButtonProps } from '@/components/shadcn/ui/button';
import { Currency } from '@/constants';
import useChainIconByCurrency from '@/hooks/useChainIconByCurrency';
import { useBtcWallet } from '@/networks/btc-like/btc';
import { cn } from '@/utils';
import { truncateMidAddressInputValue } from '@/utils/truncateAddress';

export const ConnectedBtcWalletButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }, ref) => {
    const { address } = useBtcWallet();
    const { icon } = useChainIconByCurrency(Currency.BTC);

    return (
      <Button
        ref={ref}
        size="sm"
        variant="secondary"
        className={cn('font-roboto rounded-xl font-bold', className)}
        {...props}
      >
        {children || (
          <div className="flex items-center gap-2 text-sm">
            <ChainIcon src={(icon as string) || ''} />
            <div className="hidden sm:block">{truncateMidAddressInputValue(address ?? '', 13)}</div>
          </div>
        )}
      </Button>
    );
  }
);

ConnectedBtcWalletButton.displayName = 'BtcButton';

export const ConnectedBtcWallet: FC<ButtonProps> = ({ className, children, ...props }) => {
  const { isConnected, connect } = useBtcWallet();

  if (!isConnected) {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void connect().catch(console.error);
        }}
        className={cn('font-cocogoose rounded-xl text-sm font-bold', className)}
        {...props}
      >
        {children || 'Connect BTC Wallet'}
      </Button>
    );
  }

  return (
    <ConnectedBtcWalletButton className={className} {...props}>
      {children}
    </ConnectedBtcWalletButton>
  );
};
